import { inject, injectable } from '@/inversify';
import { KEY } from '@/inversify.keys';
import type UserService from '@/modules/user/user.service';
import NumberOfGuestsStore from '@/modules/number-of-guests/store/number-of-guests.store';
import Stateable from '@/modules/common/interfaces/stateable.interface';
import NumberOfGuestsApiService, { NumberOfGuestsApiServiceS } from '@/modules/number-of-guests/number-of-guests-api.service';
import type StoreFacade from '@/modules/common/services/store-facade';
import type RatesService from '@/modules/rates/rates.service';
import { SettingsGeneralService } from '../settings/settings-general.service';

export const NumberOfGuestsServiceS = Symbol.for('NumberOfGuestsServiceS');
@injectable()
export default class NumberOfGuestsService implements Stateable {
    @inject(NumberOfGuestsApiServiceS) private numberOfGuestsApiService!: NumberOfGuestsApiService;
    @inject(KEY.UserService) private userService!: UserService;
    @inject(KEY.StoreFacade) private storeFacade!: StoreFacade;
    @inject(KEY.RatesService) private ratesService!: RatesService;
    @inject(KEY.SettingsGeneralService) private settingsGeneralService!: SettingsGeneralService;

    readonly storeState: NumberOfGuestsStore = this.storeFacade.getState('NumberOfGuestsStore');

    constructor() {
        this.storeFacade.watch(
            () => this.userService.isUserLoaded,
            this.storeState.loading.reset.bind(this.storeState.loading),
        );
    }

    get defaultNumberOfGuests() {
        return this.settingsGeneralService.defaultFilters.numberOfGuests;
    }

    get numberOfGuests() {
        if (!this.ratesService.data) {
            return [this.settingsGeneralService.defaultFilters.numberOfGuests];
        }

        return this.ratesService.data.numberOfGuestsOptions;
    }

    async loadData() {
        if (!this.userService.isHotelUser) {
            return false;
        }

        const { currentHotelId } = this.userService;
        if (currentHotelId === null) {
            return false;
        }

        const numberOfGuests = await this.numberOfGuestsApiService.getNumbersOfGuests(currentHotelId);

        this.storeState.numberOfGuests = numberOfGuests || [];
        return true;
    }
}
